import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MapPin, Truck, Lock, ArrowLeft } from "lucide-react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { trackInitiateCheckout } from "@/lib/fbPixel";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function Checkout() {
  const { items, subtotal } = useCart();
  const navigate = useNavigate();
  const [fulfillment, setFulfillment] = useState("pickup");
  const [counties, setCounties] = useState([]);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "FL", zip: "", county: "Orange" });

  useEffect(() => { api.get("/meta").then((r) => setCounties(r.data.counties || [])).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const refreshQuote = useCallback(async () => {
    if (items.length === 0) return;
    setQuoting(true);
    try {
      const { data } = await api.post("/checkout/quote", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
        fulfillment, state: form.state, county: form.county,
      });
      setQuote(data);
    } catch (e) { /* ignore */ } finally { setQuoting(false); }
  }, [items, fulfillment, form.state, form.county]);

  useEffect(() => { refreshQuote(); }, [refreshQuote]);

  const pay = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    if (fulfillment === "ship" && (!form.address || !form.city || !form.zip)) { toast.error("Shipping address is required"); return; }
    setPaying(true);
    try {
      trackInitiateCheckout(items, quote?.total ?? subtotal);
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
        fulfillment, customer: form, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPaying(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 py-24 text-center font-mono">
        Your cart is empty. <Link to="/" className="text-link underline">Browse the catalog</Link>
      </div>
    );
  }

  const isFL = form.state === "FL";

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8">
      <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-subtle hover:text-ink mb-6"><ArrowLeft className="h-4 w-4" /> Continue shopping</Link>
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mb-8">CHECKOUT</h1>

      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        {/* FORM */}
        <div className="space-y-8">
          {/* Fulfillment */}
          <div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-subtle mb-3">Fulfillment Method</div>
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="fulfillment-pickup"
                onClick={() => setFulfillment("pickup")}
                className={`border-2 p-4 text-left transition-colors ${fulfillment === "pickup" ? "border-ink bg-ink text-paper" : "border-surface2 hover:border-ink"}`}
              >
                <MapPin className="h-5 w-5 mb-2" />
                <div className="font-heading font-bold">Local Pickup</div>
                <div className="font-mono text-xs mt-1 opacity-80">FREE · $0.00 shipping</div>
              </button>
              <button
                data-testid="fulfillment-ship"
                onClick={() => setFulfillment("ship")}
                className={`border-2 p-4 text-left transition-colors ${fulfillment === "ship" ? "border-ink bg-ink text-paper" : "border-surface2 hover:border-ink"}`}
              >
                <Truck className="h-5 w-5 mb-2" />
                <div className="font-heading font-bold">UPS Ground</div>
                <div className="font-mono text-xs mt-1 opacity-80">Calculated by weight & zone</div>
              </button>
            </div>
          </div>

          {/* Contact */}
          <div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-subtle mb-3">Contact</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field testid="checkout-name" label="Full Name *" value={form.name} onChange={set("name")} />
              <Field testid="checkout-email" label="Email *" type="email" value={form.email} onChange={set("email")} />
              <Field testid="checkout-phone" label="Phone" value={form.phone} onChange={set("phone")} />
            </div>
          </div>

          {/* Address */}
          {fulfillment === "ship" ? (
            <div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-subtle mb-3">Shipping Address</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Field testid="checkout-address" label="Street Address *" value={form.address} onChange={set("address")} /></div>
                <Field testid="checkout-city" label="City *" value={form.city} onChange={set("city")} />
                <SelectField testid="checkout-state" label="State *" value={form.state} onChange={set("state")} options={US_STATES} />
                <Field testid="checkout-zip" label="ZIP *" value={form.zip} onChange={set("zip")} />
                {isFL && (
                  <SelectField testid="checkout-county" label="FL County (for tax)" value={form.county} onChange={set("county")} options={counties} />
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-subtle mb-3">Pickup Details (Florida)</div>
              <SelectField testid="checkout-county" label="FL County (for tax)" value={form.county} onChange={set("county")} options={counties} />
              <p className="font-mono text-xs text-subtle mt-2">Pickup location details will be emailed after purchase.</p>
            </div>
          )}
        </div>

        {/* SUMMARY */}
        <div className="lg:sticky lg:top-20 self-start">
          <div className="border-2 border-ink">
            <div className="bg-ink text-paper p-4 font-heading font-black text-lg tracking-tighter">ORDER SUMMARY</div>
            <div className="p-4 space-y-3 max-h-64 overflow-y-auto border-b border-surface2">
              {items.map((i) => (
                <div key={i.id} className="flex gap-3">
                  <img src={i.image} alt="" className="h-12 w-12 object-cover border border-ink" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs leading-tight line-clamp-2">{i.name}</div>
                    <div className="font-mono text-[11px] text-subtle">Qty {i.qty}</div>
                  </div>
                  <div className="font-mono text-xs font-bold">{money(i.our_price * i.qty)}</div>
                </div>
              ))}
            </div>
            <div className="p-4 space-y-2 font-mono text-sm">
              <Row label="Subtotal" value={money(quote?.subtotal ?? subtotal)} />
              {quote?.bundle_discount > 0 && (
                <div className="flex justify-between text-success">
                  <span data-testid="bundle-discount-label">Bundle -{quote.bundle_pct}% ({quote.total_qty} items)</span>
                  <span className="font-bold" data-testid="bundle-discount-amount">-{money(quote.bundle_discount)}</span>
                </div>
              )}
              {quote?.next_bundle_tier && (
                <p data-testid="bundle-nudge" className="text-[11px] text-subtle border-l-2 border-ink pl-2">
                  Add {quote.next_bundle_tier.add_qty} more item{quote.next_bundle_tier.add_qty > 1 ? "s" : ""} to save {quote.next_bundle_tier.pct}% on your order
                </p>
              )}
              <Row label={fulfillment === "pickup" ? "Shipping (Pickup)" : `UPS Ground${quote?.shipping_zone ? ` · Zone ${quote.shipping_zone}` : ""}`} value={quote ? (quote.shipping === 0 ? "FREE" : money(quote.shipping)) : "—"} />
              <Row label={quote?.tax_rate ? `FL Tax (${(quote.tax_rate * 100).toFixed(2)}%)` : "FL Sales Tax"} value={quote ? money(quote.tax) : "—"} />
              <div className="border-t-2 border-ink mt-2 pt-2 flex justify-between items-center">
                <span className="font-heading font-black text-lg tracking-tighter">TOTAL</span>
                <span data-testid="order-total" className="font-heading font-black text-2xl tracking-tighter">{quote ? money(quote.total) : "—"}</span>
              </div>
            </div>
            <div className="p-4 pt-0">
              <button
                data-testid="pay-button"
                onClick={pay}
                disabled={paying || quoting || !quote}
                className="w-full bg-ink text-paper h-14 font-mono text-sm uppercase tracking-[0.15em] shadow-hard hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Lock className="h-4 w-4" /> {paying ? "Redirecting…" : "Pay With Card"}
              </button>
              <p className="font-mono text-[10px] text-subtle text-center mt-2">Secure checkout via Stripe · Test card 4242 4242 4242 4242</p>
              <p className="font-mono text-[10px] text-subtle text-center mt-1">Items are held for 2 hours once you start checkout.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-wider text-subtle">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={onChange} className="mt-1 w-full border border-ink h-10 px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
    </label>
  );
}
function SelectField({ label, testid, value, onChange, options }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-wider text-subtle">{label}</span>
      <select data-testid={testid} value={value} onChange={onChange} className="mt-1 w-full border border-ink h-10 px-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ink bg-paper">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-subtle">{label}</span><span className="font-bold">{value}</span></div>;
}
